import { serverContext } from "js/config";
import getLocalStorage from "js/core/utilities/localStorage";
import { trackActivity } from "js/core/utilities/utilities";
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type SupportedLocale } from "./resources";

const LOCALE_STORAGE_KEY = "bai-locale";
const LOCALE_COOKIE_NAME = "locale";
const BROWSER_LOCALE_TRACKED_KEY = "bai-browser-locale-tracked";

function isSupportedLocale(locale?: string | null): locale is SupportedLocale {
    return !!locale && SUPPORTED_LOCALES.includes(locale as SupportedLocale);
}

function readStoredLocale() {
    if (typeof window !== "undefined") {
        const storage = getLocalStorage();
        return storage.getItem(LOCALE_STORAGE_KEY);
    }
    return null;
}

function writeStoredLocale(locale: SupportedLocale) {
    if (typeof window !== "undefined") {
        const storage = getLocalStorage();
        storage.setItem(LOCALE_STORAGE_KEY, locale);
    }
}

function setLocaleCookie(locale: SupportedLocale) {
    if (typeof document === "undefined") {
        return;
    }

    const maxAge = 60 * 60 * 24 * 365; // one year
    document.cookie = `${LOCALE_COOKIE_NAME}=${locale}; path=/; max-age=${maxAge}`;
    document.documentElement?.setAttribute("lang", locale);
}

function matchSupportedLocale(locale?: string | null): SupportedLocale | null {
    if (!locale) {
        return null;
    }

    const normalized = locale.toLowerCase();
    const exact = SUPPORTED_LOCALES.find(l => l.toLowerCase() === normalized);
    if (exact) {
        return exact;
    }

    const primary = normalized.split("-")[0];
    return SUPPORTED_LOCALES.find(l => l.toLowerCase().startsWith(primary)) || null;
}

function trackBrowserLocaleDetection(storedLocale: string | null) {
    if (typeof window === "undefined") {
        return;
    }

    const storage = getLocalStorage();
    if (storage.getItem(BROWSER_LOCALE_TRACKED_KEY)) {
        return;
    }

    const browserLocale = window.navigator?.languages?.[0] || window.navigator?.language;
    if (!browserLocale) {
        return;
    }

    const matchedLocale = matchSupportedLocale(browserLocale);
    try {
        trackActivity("UserPreferences", "BrowserLanguageDetected", null, null, {
            browser_locale: browserLocale,
            matched_locale: matchedLocale,
            stored_locale: storedLocale,
            server_locale: serverContext.locale ?? null
        });
    } catch (err) {
        console.warn("[i18n] failed to track browser locale detection", err);
    }

    storage.setItem(BROWSER_LOCALE_TRACKED_KEY, "true");
}

export function getInitialLocale(): SupportedLocale {
    const stored = readStoredLocale();
    trackBrowserLocaleDetection(stored);
    if (isSupportedLocale(stored)) {
        return stored;
    }

    if (isSupportedLocale(serverContext.locale)) {
        return serverContext.locale;
    }

    return DEFAULT_LOCALE;
}

export function getAvailableLocales(): SupportedLocale[] {
    const fromServer = serverContext.availableLocales;
    if (Array.isArray(fromServer) && fromServer.length > 0) {
        return fromServer.filter(isSupportedLocale) as SupportedLocale[];
    }
    return SUPPORTED_LOCALES;
}

export function setPreferredLocale(locale: SupportedLocale) {
    if (!isSupportedLocale(locale)) {
        return;
    }

    serverContext.locale = locale;
    serverContext.availableLocales = getAvailableLocales();
    writeStoredLocale(locale);
    setLocaleCookie(locale);
}
